<template>
  <section id="testimonials" class="pt-4 md:pt-6 pb-12 md:pb-16 bg-primary/10">
    <div class="container mx-auto px-4 sm:px-6">
      <h2 class="text-3xl sm:text-4xl md:text-4xl font-bold text-center mb-2 md:mb-3 text-primary">Quotes</h2>

      <div class="relative max-w-5xl mx-auto">
        <transition name="fade" mode="out-in">
          <div :key="currentTestimonial" class="testimonial-card">
            <Card class="bg-white shadow-lg">
              <CardContent class="p-6 md:p-8 lg:p-10">
                <div class="flex flex-col md:flex-row gap-6 md:gap-8 items-center md:items-start">
                  <!-- Avatar Section -->
                  <div class="flex-shrink-0">
                    <div v-if="testimonials[currentTestimonial].image && isValidImage(testimonials[currentTestimonial].image)"
                         class="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full overflow-hidden ring-4 ring-primary/20 shadow-lg">
                      <img
                        :src="testimonials[currentTestimonial].image"
                        :alt="testimonials[currentTestimonial].name"
                        class="w-full h-full object-cover"
                        @error="handleImageError"
                      >
                    </div>
                    <div v-else
                         class="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full bg-primary text-white flex items-center justify-center text-2xl sm:text-3xl md:text-4xl font-bold shadow-lg ring-4 ring-primary/20">
                      {{ getInitials(testimonials[currentTestimonial].name) }}
                    </div>
                  </div>

                  <!-- Content Section -->
                <div class="flex-1 text-center md:text-left">
                    <div class="mb-4 md:mb-6">
                      <svg class="w-8 h-8 sm:w-10 sm:h-10 text-primary/30 mx-auto md:mx-0 mb-3 md:mb-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.996 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.984zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
                      </svg>
                      <p class="text-base sm:text-lg md:text-xl text-gray-700 italic leading-relaxed">
                        "{{ testimonials[currentTestimonial].quote }}"
                      </p>
                    </div>

                    <div class="pt-3 md:pt-4 border-t border-gray-200">
                      <p class="font-bold text-lg md:text-xl text-safari-black mb-1">
                        {{ testimonials[currentTestimonial].name }}
                      </p>
                      <p class="text-primary font-medium text-sm md:text-base">
                        {{ testimonials[currentTestimonial].role }}
                      </p>
                      <p v-if="testimonials[currentTestimonial].location" class="text-xs sm:text-sm text-gray-500 mt-1">
                        {{ testimonials[currentTestimonial].location }}
                      </p>
                    </div>
                  </div>
              </div>
            </CardContent>
          </Card>
        </div>
        </transition>

        <!-- Navigation Dots -->
        <div class="flex justify-center mt-8 gap-3">
          <button
            v-for="(testimonial, index) in testimonials"
            :key="index"
            @click="goToTestimonial(index)"
            :class="[
              'w-3 h-3 rounded-full transition-all duration-300',
              currentTestimonial === index
                ? 'bg-primary w-8'
                : 'bg-gray-300 hover:bg-primary/50'
            ]"
            :aria-label="`View testimonial from ${testimonial.name}`"
          >
          </button>
        </div>

        <!-- Navigation Arrows -->
        <button
          @click="previousTestimonial"
          class="absolute left-0 md:-left-12 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-primary hover:text-white transition-colors group"
          aria-label="Previous testimonial"
        >
          <svg class="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
        <button
          @click="nextTestimonial"
          class="absolute right-0 md:-right-12 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-primary hover:text-white transition-colors group"
          aria-label="Next testimonial"
        >
          <svg class="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { Card, CardContent } from '@/components/ui/card'

interface Testimonial {
  name: string
  role: string
  quote: string
  image?: string
  location?: string
}

@Options({
  components: { Card, CardContent },
  data() {
    return {
      currentTestimonial: 0,
      imageErrors: new Set<number>(),
      testimonials: [
        {
          name: 'Dr. Anne Bradley',
          role: 'Former CIA Economist',
          location: '',
          quote: 'Economic growth requires institutions that recognize people have dignity, that people are equal, and that work is the only way to lift people up permanently from poverty.',
          image: '/images/Partners/Anne-Bradley-Headshot-Web.jpg'
        },
        {
          name: 'Elie Imani',
          role: 'Founder',
          location: '',
          quote: 'I have seen many organizations lead with good intentions while neglecting what is practical. At Safari Strives, we do both, the right thing and the workable thing, for our sisters.',
          image: '/images/TeamMembersHeadshots/elieimani.jpg'
        },
        {
          name: 'Mbabazi Ange',
          role: 'Employee',
          location: '',
          quote: 'Before Safari Strives I moved between small informal jobs and could not plan ahead. At the Safari TechStyle Hub I gained training and a formal job, and now I earn a steady income and can build my future with confidence.',
          image: '/images/TeamMembersHeadshots/MbabaziAnge.jpeg'
        },
        {
          name: 'Elyse Nshimiyimana',
          role: 'Good Governance Officer',
          location: '',
          quote: 'I thank Safari Strives by supporting local innovators and empowering women, you are strengthening every family in this community.',
          image: '/images/TeamMembersHeadshots/ElyseNshimiyimana.jpg'
        }
      ] as Testimonial[],
      interval: null as any
    }
  },
  mounted() {
    this.startAutoPlay()
  },
  unmounted() {
    if (this.interval) clearInterval(this.interval)
  },
  methods: {
    getInitials(name: string): string {
      const words = name.trim().split(' ')
      if (words.length >= 2) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },
    isValidImage(imagePath: string | undefined): boolean {
      if (!imagePath || imagePath === '' || imagePath.includes('/path/to/')) {
        return false
      }
      return !this.imageErrors.has(this.currentTestimonial)
    },
    handleImageError() {
      this.imageErrors.add(this.currentTestimonial)
    },
    nextTestimonial() {
      this.currentTestimonial = (this.currentTestimonial + 1) % this.testimonials.length
      this.restartAutoPlay()
    },
    previousTestimonial() {
      this.currentTestimonial = this.currentTestimonial === 0
        ? this.testimonials.length - 1
        : this.currentTestimonial - 1
      this.restartAutoPlay()
    },
    goToTestimonial(index: number) {
      this.currentTestimonial = index
      this.restartAutoPlay()
    },
    startAutoPlay() {
      if (this.interval) clearInterval(this.interval)
      this.interval = setInterval(() => {
        this.nextTestimonial()
      }, 6000)
    },
    restartAutoPlay() {
      this.startAutoPlay()
    }
  }
})
export default class Testimonials extends Vue {}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.testimonial-card {
  min-height: 300px;
}

@media (max-width: 640px) {
  .testimonial-card {
    min-height: 350px;
  }
}

@media (max-width: 768px) {
  .testimonial-card {
    min-height: 380px;
  }
}
</style>
